/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import * as moment from 'moment';

import {isPresent} from '../../utils/lang';
import {
  FormDescriptor,
  FormValues,
  GroupZoneDescriptor,
  TabSheetDescriptor,
  Value,
  ZoneDescriptor,
  ZoneValues
} from '../form-generator.interfaces';
import {FormGeneratorService} from '../form-generator.service';

export class DataTransformer {

  private pmGroupZones: Map<string, GroupZoneDescriptor> = new Map();

  public static zoneTypeHasData(type: string) {
    return type !== 'BLANK' && type !== 'BREAK_LINE';
  }

  public static applyMask(value: string, mask: string): string {
    if (DataTransformer.isMasked(value, mask)) {
      return value;
    }

    if ((mask.match(/s/g) || []).length !== value.length) {
      return value;
    }

    let i = 0;
    return mask.split('').reduce((result: string, s: string) => {
      if (s === 's') {
        if (isPresent(value[i])) {
          result += value[i];
        }
        i++;
      } else {
        result += s;
      }
      return result;
    }, '');
  }

  public static extractMask(value: string, mask: string): string {
    if (!DataTransformer.isMasked(value, mask)) {
      return value;
    }
    return value
      .split('')
      .reduce((acc: string, val: string, index: number) => {
        const m = mask[index];
        if (m === 's' || !m) {
          acc += val;
        }
        return acc;
      }, '');
  }

  private static isMasked(value: string, mask: string): boolean {
    if (!value || !mask) {
      return false;
    }
    if (value.length !== mask.length) {
      return false;
    }
    for (let i = 0; i < mask.length; i++) {
      if (mask[i] !== 's' && value[i] !== mask[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * The method createZoneValues takes a GroupZoneDescriptor
   * From this GroupZoneDescriptor, transform any object (ex: an entity) to a list of ZoneValues.
   * It produces as many values  as zones from the GroupZoneDescriptor
   */

  public static createZoneValues(groupZone: GroupZoneDescriptor,
                                 formGeneratorService: FormGeneratorService,
                                 dataForZone?,
                                 id?): ZoneValues[] {

    if (!dataForZone || dataForZone.length === 0) {
      dataForZone = formGeneratorService.createEmptyData(groupZone, id);
    }

    const preparedDataForZone = dataForZone
      .sort((a, b) => {
        if (
          a.begindate === undefined ||
          b.begindate === undefined ||
          moment(b.begindate).diff(moment(a.begindate)) === 0
        ) {
          if (a.id === undefined || b.id === undefined) {
            return 0;
          } else {
            return a.id - b.id;
          }
        } else {
          return moment(b.begindate).diff(moment(a.begindate));
        }
      })
      .map((zoneData: any) => {
        return {
          begindate: zoneData.begindate,
          isDated: zoneData.isDated,
          enddate: zoneData.enddate,
          groupName: zoneData.groupName,
          id: zoneData.id,
          default: zoneData.default,
          values: Object.assign(zoneData.values, DataTransformer.valuesForZones(groupZone.zones, zoneData))
        };
      });

    if (groupZone.groupZoneID === 'WorkCycle') {
      preparedDataForZone.forEach(data => {
        const metadata = groupZone.metadatas.items.find(item => item.cycleId === data.values['WorkCycle'].value);
        if (metadata) {
          data.values['workCycleTable'].value = [...metadata.items];
          data.values['refDate'].value = metadata.refdate;
          data.values['workCycleTableType'].value = [...metadata.typeOfItems];
        }
      });
    }
    return preparedDataForZone;
  }

  /**
   * Get transformed (prepared) values for zones
   *
   * @param zones the list of zones to get values for
   * @param data the matching data for the zones
   * @param root the root data
   * @returns {string: Value} the transformed values
   */
  public static valuesForZones(zones: Array<ZoneDescriptor>, data: { values: { string: any } }): { string: Value } {
    return zones
      .filter(zone => DataTransformer.zoneTypeHasData(zone.type))
      .reduce((values, zone: ZoneDescriptor) => {
        values[zone.zoneID] = Object.assign({type: zone.type, value: ''}, data.values[zone.zoneID] || {});

        const zoneValue = values[zone.zoneID];
        if (zone.type === 'BOOLEAN') {
          zoneValue.value =
            (zoneValue.value === 'true'
              || zoneValue.value === true
              || (zoneValue.value === '' && zone.defaultValue === 'true')) ? true
              :
              (zoneValue.value === 'false'
                || zoneValue.value === false
                || (zoneValue.value === '' && zone.defaultValue === 'false')) ? false
                :
                null;
        }

        if (zone.type === 'PERCENTAGE') {
          zoneValue.value = parseFloat(zoneValue.value);
          if (isNaN(zoneValue.value)) {
            zoneValue.value = '';
          }
        }

        if (zone.type === 'WORK_CYCLE_TABLE' && (<any>data.values).workCycleTableType && zoneValue.value) {
          const typeOfItems = (<any>data.values).workCycleTableType.value;
          zoneValue.value = zoneValue.value.map((v, index: number) => {
            if (v === 0 && typeOfItems[index] === 'OFF') {
              return 25;
            }
            return v;
          });
        }

        return values;
      }, <{ string: Value }>{});
  }

  constructor(private descriptor: FormDescriptor,
              private dataKey: string,
              private formGeneratorService: FormGeneratorService) {

    this.descriptor.tabSheets.forEach((tabSheet: TabSheetDescriptor) => {
      tabSheet.groupZones
        .filter((groupZone: GroupZoneDescriptor) => groupZone.internalType === 'BANKING_DETAILS')
        .forEach((groupZone: GroupZoneDescriptor) => {

          if (!groupZone.groupZoneID.includes('_')) {
            groupZone.zones = groupZone.metadatas.descriptors.get('default').zones;
            return;
          }

          const groupName: string = groupZone.groupZoneID.split(':')[1].split('_')[0];
          let pmGroupZone: GroupZoneDescriptor = this.pmGroupZones.get(groupName);

          if (groupZone === pmGroupZone) {
            return;
          }

          groupZone.zones = groupZone.zones.map((z: ZoneDescriptor) => {
            return Object.assign({}, z, {zoneID: z.zoneID.split('_')[0]});
          });

          if (!pmGroupZone) {
            pmGroupZone = this.createPaymentsModalityGroupZone(groupZone, groupName);
            tabSheet.groupZones.push(pmGroupZone);
          }

          pmGroupZone.metadatas.descriptors.set(groupZone.groupZoneID.split('_')[1], groupZone);
        });

      tabSheet.groupZones = tabSheet.groupZones
        .filter((groupZone: GroupZoneDescriptor) => {
          return groupZone.internalType !== 'BANKING_DETAILS' || !groupZone.groupZoneID.includes('_');
        });
    });
  }

  /**
   * @param root the root data to transform
   * @returns {FormValues} the constructed form values map, ready for consumption by the form generator
   */
  public transform(root: any): FormValues {
    if (!root) {
      root[this.dataKey] = [];
    }

    const result = new Map<string, Array<ZoneValues>>(),
      data = root[this.dataKey] || root.attributes[this.dataKey],
      flattenedZones = [].concat.apply([],
        this.descriptor.tabSheets.map((tabSheet: TabSheetDescriptor) => tabSheet.groupZones));

    data.forEach((zoneData: any) => {
      if (zoneData.groupName.split(':')[0] === 'BankingDetails') {
        zoneData.groupName = zoneData.groupName.split('_')[0];
        const newValues = {};
        Object.keys(zoneData.values).forEach((key: string) => {
          newValues[key.split('_')[0]] = zoneData.values[key];
        });
        zoneData.values = newValues;
      }
    });

    // Transform zones
    flattenedZones.forEach((groupZone: GroupZoneDescriptor) => {
      const dataForZone = data.filter((zoneData: any) => zoneData.groupName === groupZone.groupZoneID);

      result.set(
        groupZone.groupZoneID,
        DataTransformer.createZoneValues(groupZone, this.formGeneratorService, dataForZone));
    });

    return result;
  }

  private createPaymentsModalityGroupZone(descriptor: GroupZoneDescriptor, groupName: string): GroupZoneDescriptor {
    const keyZone: ZoneDescriptor = descriptor.zones
      .filter((zone: ZoneDescriptor) => zone.isKeyZone || zone.zoneID.includes('.paymentModality'))[0];

    const paymentsModalityGroupZone: GroupZoneDescriptor = Object.assign(
      {},
      descriptor,
      {
        // zoneID: keyZone.zoneID.split('_')[0],
        groupZoneID: descriptor.groupZoneID.split('_')[0],
        zones: [keyZone]
      }
    );

    const pmDescriptors: Map<string, GroupZoneDescriptor> = new Map();
    pmDescriptors.set('default', <GroupZoneDescriptor>{zones: [keyZone]});

    Object.assign(paymentsModalityGroupZone.metadatas, {descriptors: pmDescriptors});

    this.pmGroupZones.set(groupName, paymentsModalityGroupZone);

    return paymentsModalityGroupZone;
  }
}
